import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Divider,
  Tooltip,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Edit as EditIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  Inventory as InventoryIcon,
  LocalShipping as ShippingIcon,
  Flag as FlagIcon,
  CheckCircle as CheckCircleIcon,
  Palette as PaletteIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  DoneAll as FinalizeIcon,
  PhoneDisabled as PhoneDisabledIcon,
  ContentCopy as ContentCopyIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { formatDateBR, formatDateLongBR, formatDateTimeBR } from '../utils/dateUtils';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/api';

/** Extrai itens da descrição (separados por vírgula ou quebra de linha) para exibição organizada */
function parseOrderDescriptionItems(description: string | undefined): string[] {
  if (!description || typeof description !== 'string') return [];
  const combined = description
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split(/[,\n]/)
    .map((s) => s.replace(/^\s*[-•]\s*/, '').trim())
    .filter(Boolean);
  return combined;
}

/** Botão que chama a API para baixar a logo (com autenticação) */
function LogoDownloadButton({ storeId, irrobaOrderId, fileId }: { storeId: string; irrobaOrderId: string; fileId: string }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <>
      <Button
        size="small"
        variant="outlined"
        startIcon={loading ? undefined : <DownloadIcon />}
        disabled={loading}
        onClick={async () => {
          setLoading(true);
          setError(null);
          try {
            await apiService.downloadOrderLogo(storeId, irrobaOrderId, fileId);
          } catch (err: any) {
            let msg = 'Erro ao baixar logo';
            const data = err.response?.data;
            if (typeof data === 'string') msg = data;
            else if (data?.error) msg = data.error;
            else if (data instanceof Blob) {
              try { const j = JSON.parse(await data.text()); msg = j.error || msg; } catch { /* ignore */ }
            }
            setError(msg);
          } finally {
            setLoading(false);
          }
        }}
        sx={{ minWidth: 'auto', py: 0.25, px: 1, fontSize: '0.75rem' }}
      >
        {loading ? 'Baixando...' : 'Baixar logo'}
      </Button>
      {error && (
        <Typography variant="caption" sx={{ color: 'error.main', display: 'block', width: '100%' }}>
          {error}
        </Typography>
      )}
    </>
  );
}

/** Detecta se é referência à logo do cliente ("ANEXAR SUA LOGO AQUI!") */
const isLogoReference = (label: string | null) =>
  label && /anexar\s+sua\s+logo/i.test(label.trim());

/** Verifica se o valor parece URL ou ID de arquivo (hash alfanumérico 20+ chars) */
const isDownloadableLogoValue = (value: string) => {
  if (!value || value.length < 20) return false;
  if (/^https?:\/\//i.test(value)) return true; // URL direta
  if (/^[a-f0-9]{20,}$/i.test(value)) return true; // Hash/ID de arquivo
  return false;
};

/** Componente que exibe a descrição do pedido de forma organizada para o designer */
function OrderDescriptionDisplay({
  description,
  storeId,
  irrobaOrderId,
}: {
  description: string | undefined;
  storeId?: string;
  irrobaOrderId?: string;
}) {
  const items = parseOrderDescriptionItems(description);
  const canDownloadLogo = Boolean(storeId && irrobaOrderId);

  if (items.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
        Nenhuma descrição informada
      </Typography>
    );
  }
  return (
    <Box
      sx={{
        backgroundColor: '#f8fafc',
        borderRadius: 2,
        border: '1px solid #e2e8f0',
        p: 2,
        '& > * + *': { mt: 1.5 },
      }}
    >
      {items.map((item, index) => {
        const colonMatch = item.match(/^([^:(]+?)\s*(?:\?:|:)\s*(.+)$/s);
        const hasLabel = colonMatch != null;
        const label = hasLabel ? colonMatch![1].trim() : null;
        const value = hasLabel ? colonMatch![2].trim() : item;
        const isLogo = isLogoReference(label) && isDownloadableLogoValue(value);

        return (
          <Box
            key={index}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.25,
              pb: index < items.length - 1 ? 1.5 : 0,
              borderBottom: index < items.length - 1 ? '1px solid #e2e8f0' : 'none',
            }}
          >
            {hasLabel && label ? (
              <>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {label}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="body2" sx={{ color: '#1e293b', fontWeight: 500 }}>
                    {value || '—'}
                  </Typography>
                  {isLogo && (
                    /^https?:\/\//i.test(value) ? (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        href={value}
                        component="a"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ minWidth: 'auto', py: 0.25, px: 1, fontSize: '0.75rem' }}
                      >
                        Baixar logo
                      </Button>
                    ) : canDownloadLogo ? (
                      <LogoDownloadButton storeId={storeId!} irrobaOrderId={irrobaOrderId!} fileId={value} />
                    ) : null
                  )}
                </Box>
              </>
            ) : (
              <Typography variant="body2" sx={{ color: '#1e293b', fontWeight: 500 }}>
                {item}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

interface Order {
  id: string;
  orderNumber: string;
  clientName: string;
  clientDocument: string;
  clientPhone: string;
  hasArt: boolean;
  workStatus:
    | 'Aguardando'
    | 'Em Andamento'
    | 'Finalizado'
    | 'Sem Retorno'
    | 'Aguardando Ap'
    | 'Aguardando arte'
    | 'Vendedor'
    | 'Suporte';
  shippingMethod: 'Retirada' | 'Entrega Local' | 'Transportadora' | 'Sedex' | 'PAC';
  priority: 'Baixa' | 'Normal' | 'Alta' | 'Urgente';
  orderValue: number;
  deadline: string;
  enteredAt: string;
  description: string;
  comment: string;
  artCount: number;
  source?: 'irroba' | 'local';
  irrobaStatus?: string;
  storeId?: string;
  irrobaOrderId?: string;
  storeName?: string;
}

type DesignerStatus = 'Suave' | 'Fluxo Bom' | 'Correria' | 'Sobrecarregado' | 'Precisando de Apoio';
type WorkStatusFilter =
  | 'Todos'
  | 'Aguardando'
  | 'Em Andamento'
  | 'Finalizado'
  | 'Sem Retorno'
  | 'Aguardando Ap'
  | 'Aguardando arte'
  | 'Vendedor'
  | 'Suporte';

const WORK_STATUS_OPTIONS: Order['workStatus'][] = [
  'Aguardando',
  'Em Andamento',
  'Finalizado',
  'Sem Retorno',
  'Aguardando Ap',
  'Aguardando arte',
  'Vendedor',
  'Suporte',
];

const DesignerInterface: React.FC = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const viewDesignerId = searchParams.get('designerId');
  const viewDesignerName = searchParams.get('designerName');
  const isViewingOtherDesigner = viewDesignerId && user?.role === 'ADMIN' && viewDesignerId !== user.id;
  // Suporte: ao clicar em "Ver pedidos do Designer" no Atendimento Arte, pode editar SOMENTE a coluna Prioridade
  // Suporte, Gerência e Produção: ao clicar em "Ver pedidos do Designer", podem editar SOMENTE a coluna Prioridade
  const isSupportOrManagerViewingDesigner = !!viewDesignerId && (user?.role === 'SUPPORT' || user?.role === 'MANAGER' || user?.role === 'PRODUCAO');

  const [myCurrentStatus, setMyCurrentStatus] = useState<DesignerStatus>('Fluxo Bom');
  const [openStatusDialog, setOpenStatusDialog] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [orders, setOrders] = useState<Order[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [statusFilter, setStatusFilter] = useState<WorkStatusFilter>('Todos');
  const [storeFilter, setStoreFilter] = useState<string>('');
  const [orderNumberSearch, setOrderNumberSearch] = useState('');
  const [openFinalizeDialog, setOpenFinalizeDialog] = useState(false);
  const [finalizeArtsCount, setFinalizeArtsCount] = useState(0);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState<'workStatus' | 'shipping' | 'priority' | 'details' | 'deadline'>('details');
  const [ordersBlockedMessage, setOrdersBlockedMessage] = useState<string | null>(null);
  // Artes "por fora" (Shopee etc.) — total de HOJE, ajustável
  const [externalArtsInput, setExternalArtsInput] = useState('0');
  const [externalArtsSaving, setExternalArtsSaving] = useState(false);
  // Designer alvo para as artes externas (próprio designer, ou o que está sendo visualizado)
  const externalTargetId = (isViewingOtherDesigner || isSupportOrManagerViewingDesigner)
    ? viewDesignerId
    : (user?.role === 'DESIGNER' ? user?.id : null);
  // Só o próprio designer (na sua interface) pode lançar; visualização é somente leitura
  const canEditExternal = !viewDesignerId && user?.role === 'DESIGNER';
  const todayStr = new Date().toISOString().split('T')[0];

  const mapStatusToBackend = (status: DesignerStatus): 'GOOD_FLOW' | 'NORMAL' | 'BUSY' | 'SLOW' => {
    switch (status) {
      case 'Suave': return 'GOOD_FLOW';
      case 'Fluxo Bom': return 'NORMAL';
      case 'Correria': return 'BUSY';
      case 'Sobrecarregado': return 'SLOW';
      case 'Precisando de Apoio': return 'SLOW';
      default: return 'NORMAL';
    }
  };

  const mapStatusFromBackend = (status: string): DesignerStatus => {
    switch (status) {
      case 'GOOD_FLOW': return 'Suave';
      case 'NORMAL': return 'Fluxo Bom';
      case 'BUSY': return 'Correria';
      case 'SLOW': return 'Sobrecarregado';
      default: return 'Fluxo Bom';
    }
  };

  // Carregar status atual do designer
  useEffect(() => {
    const loadDesignerStatus = async () => {
      const targetDesignerId = isViewingOtherDesigner ? viewDesignerId : user?.id;
      if (!targetDesignerId || (!isViewingOtherDesigner && user?.role !== 'DESIGNER')) return;
      try {
        const info = await apiService.getDesignerServiceInfo();
        const designerInfo = info.find((d: any) => d.id === targetDesignerId);
        if (designerInfo) {
          setMyCurrentStatus(mapStatusFromBackend(designerInfo.status));
        }
      } catch (error) {
        console.error('Erro ao carregar status do designer:', error);
      }
    };
    loadDesignerStatus();
  }, [user, viewDesignerId, isViewingOtherDesigner]);

  // Carregar as artes externas de HOJE do designer alvo
  useEffect(() => {
    if (!externalTargetId) return;
    (async () => {
      try {
        const data = await apiService.getDesignerExternalArts(externalTargetId, todayStr);
        setExternalArtsInput(String(data.count ?? 0));
      } catch (error) {
        console.error('Erro ao carregar artes por fora:', error);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalTargetId]);

  // Salvar (upsert) as artes externas de HOJE
  const saveExternalArts = async () => {
    if (!externalTargetId || !canEditExternal) return;
    const count = Math.max(0, parseInt(externalArtsInput) || 0);
    setExternalArtsSaving(true);
    try {
      const data = await apiService.setDesignerExternalArts(externalTargetId, todayStr, count);
      setExternalArtsInput(String(data.count));
      setSnackbar({ open: true, message: `Artes por fora salvas: ${data.count}`, severity: 'success' });
    } catch (error) {
      console.error('Erro ao salvar artes por fora:', error);
      setSnackbar({ open: true, message: 'Erro ao salvar artes por fora', severity: 'error' });
    } finally {
      setExternalArtsSaving(false);
    }
  };

  const handleStatusChange = async (status: DesignerStatus) => {
    if (!user || user.role !== 'DESIGNER' || isViewingOtherDesigner) return;
    setLoadingStatus(true);
    try {
      const backendStatus = mapStatusToBackend(status);
      await apiService.updateDesignerFlowStatus(user.id, backendStatus);
      setMyCurrentStatus(status);
      setOpenStatusDialog(false);
    } catch (error) {
      console.error('Erro ao salvar status:', error);
      setSnackbar({ open: true, message: 'Erro ao salvar status', severity: 'error' });
    } finally {
      setLoadingStatus(false);
    }
  };

  // Carregar pedidos do IRROBA para o designer
  // silent = true: atualização em background (polling/voltar à aba) sem mostrar loading
  const loadDesignerOrders = useCallback(async (silent = false) => {
    // Suporte, Gerência e Produção: ver pedidos quando vem do Atendimento Arte (com designerId)
    const targetDesignerId = (isViewingOtherDesigner || isSupportOrManagerViewingDesigner) ? viewDesignerId : (user?.role === 'DESIGNER' ? user?.id : null);
    if (!targetDesignerId) {
      if (!silent) setLoadingOrders(false);
      return;
    }
    try {
      if (!silent) {
        setLoadingOrders(true);
        setOrdersBlockedMessage(null);
      }
      const result = await apiService.getDesignerIrrobaOrders(targetDesignerId);
      const irrobaOrders = result.data || [];

      if (result.blocked && result.message) {
        setOrdersBlockedMessage(result.message);
      }

      const convertedOrders: Order[] = irrobaOrders.map((order: any) => {
        let clientDocument = order.customerDocument || '-';
        if (clientDocument === '-' && order.rawData) {
          try {
            const rawData = typeof order.rawData === 'string' ? JSON.parse(order.rawData) : order.rawData;
            clientDocument = rawData.extractedDocument || rawData.cpf || rawData.cnpj || rawData.document || '-';
          } catch (e) { /* ignore */ }
        }
        if (!clientDocument || clientDocument === '') clientDocument = '-';

        let clientPhone = order.customerPhone || '-';
        if (clientPhone === '-' && order.rawData) {
          try {
            const rawData = typeof order.rawData === 'string' ? JSON.parse(order.rawData) : order.rawData;
            clientPhone = rawData.extractedPhone || rawData.cellphone || rawData.telephone || rawData.phone || '-';
          } catch (e) { /* ignore */ }
        }
        if (!clientPhone || clientPhone === '') clientPhone = '-';

        return {
          id: order.id,
          orderNumber: order.orderNumber || `#${order.irrobaOrderId}`,
          clientName: order.customerName || 'Cliente não informado',
          clientDocument,
          clientPhone,
          hasArt: false,
          workStatus: (order.workStatus || 'Aguardando') as Order['workStatus'],
          shippingMethod: 'Retirada' as const,
          priority: ((order.priority || 'Normal') as Order['priority']),
          orderValue: parseFloat(order.totalValue) || 0,
          deadline: order.irrobaCreatedAt ? new Date(new Date(order.irrobaCreatedAt).getTime() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] : '',
          enteredAt: order.irrobaCreatedAt || order.createdAt || '',
          description: order.items || 'Pedido IRROBA',
          comment: order.comment || '',
          artCount: order.artCount || 0,
          source: 'irroba' as const,
          irrobaStatus: order.status,
          storeId: order.storeId || order.store?.id,
          storeName: order.store?.name,
          irrobaOrderId: order.irrobaOrderId,
        };
      });

      setOrders(convertedOrders);
    } catch (error) {
      if (!silent) console.error('Erro ao carregar pedidos do IRROBA:', error);
      if (!silent) setOrders([]);
    } finally {
      if (!silent) setLoadingOrders(false);
    }
  }, [user, viewDesignerId, isViewingOtherDesigner, isSupportOrManagerViewingDesigner]);

  // Carregamento inicial - Support também precisa de designerId para carregar
  useEffect(() => {
    if (viewDesignerId || (!viewDesignerId && user?.role === 'DESIGNER')) {
      loadDesignerOrders();
    }
  }, [loadDesignerOrders, viewDesignerId, user?.role]);

  // Atualização em tempo quase real: polling a cada 15s + refetch ao voltar à aba
  useEffect(() => {
    const interval = setInterval(() => {
      loadDesignerOrders(true);
    }, 15000);
    return () => clearInterval(interval);
  }, [loadDesignerOrders]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadDesignerOrders(true);
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [loadDesignerOrders]);

  // --- Persist workStatus to backend ---
  const handleUpdateWorkStatus = async (newWorkStatus: Order['workStatus']) => {
    if (!selectedOrder) return;
    try {
      await apiService.updateStoreOrderStatus(selectedOrder.id, {
        workStatus: newWorkStatus,
        ...(selectedOrder.storeId && { storeId: selectedOrder.storeId }),
      });
      setOrders(orders.map(order =>
        order.id === selectedOrder.id ? { ...order, workStatus: newWorkStatus } : order
      ));
      handleCloseDialog();
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      setSnackbar({ open: true, message: 'Erro ao atualizar status do pedido', severity: 'error' });
    }
  };

  // --- Persist artCount to backend (debounced via onBlur) ---
  const handleArtCountBlur = async (orderId: string, newCount: number) => {
    try {
      await apiService.updateStoreOrderStatus(orderId, { artCount: newCount });
    } catch (error) {
      console.error('Erro ao salvar artCount:', error);
    }
  };

  const handleUpdateDeadline = (newDeadline: string) => {
    if (selectedOrder) {
      setOrders(orders.map(order =>
        order.id === selectedOrder.id ? { ...order, deadline: newDeadline } : order
      ));
      handleCloseDialog();
    }
  };

  const handleUpdateShipping = (newShipping: Order['shippingMethod']) => {
    if (selectedOrder) {
      setOrders(orders.map(order =>
        order.id === selectedOrder.id ? { ...order, shippingMethod: newShipping } : order
      ));
      handleCloseDialog();
    }
  };

  const handleUpdatePriority = async (newPriority: Order['priority']) => {
    if (!selectedOrder) return;
    try {
      if (selectedOrder.source === 'irroba') {
        await apiService.updateStoreOrderStatus(selectedOrder.id, {
          priority: newPriority,
          ...(selectedOrder.storeId && { storeId: selectedOrder.storeId }),
        });
      }
      setOrders(orders.map(order =>
        order.id === selectedOrder.id ? { ...order, priority: newPriority } : order
      ));
      setSnackbar({ open: true, message: 'Prioridade atualizada!', severity: 'success' });
      handleCloseDialog();
    } catch (error) {
      console.error('Erro ao atualizar prioridade:', error);
      setSnackbar({ open: true, message: 'Erro ao atualizar prioridade', severity: 'error' });
    }
  };

  // --- Finalizar Dia ---
  const handleOpenFinalizeDialog = () => {
    const autoSum = orders.reduce((sum, o) => sum + o.artCount, 0);
    setFinalizeArtsCount(autoSum);
    setOpenFinalizeDialog(true);
  };

  const handleFinalizeDay = async () => {
    const targetDesignerId = isViewingOtherDesigner ? viewDesignerId : user?.id;
    if (!targetDesignerId) return;
    try {
      const today = new Date().toISOString().split('T')[0];
      const result = await apiService.finalizeDesignerDay(targetDesignerId, today, finalizeArtsCount);
      setSnackbar({
        open: true,
        message: `Dia finalizado! ${result.artsCount} artes, ${result.totalOrdersWorked} pedidos trabalhados.`,
        severity: 'success',
      });
      setOpenFinalizeDialog(false);
    } catch (error: any) {
      console.error('Erro ao finalizar dia:', error);
      setSnackbar({ open: true, message: 'Erro ao finalizar dia', severity: 'error' });
    }
  };

  const handleOpenDialog = (order: Order, type: 'workStatus' | 'shipping' | 'priority' | 'details' | 'deadline') => {
    setSelectedOrder(order);
    setDialogType(type);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedOrder(null);
  };

  // --- Counts ---
  const workStatusCounts = {
    aguardando: orders.filter(o => o.workStatus === 'Aguardando').length,
    emAndamento: orders.filter(o => o.workStatus === 'Em Andamento').length,
    finalizado: orders.filter(o => o.workStatus === 'Finalizado').length,
    semRetorno: orders.filter(o => o.workStatus === 'Sem Retorno').length,
  };
  const totalArts = orders.reduce((sum, order) => sum + order.artCount, 0);

  // --- Filter orders (status + loja + número do pedido) ---
  const byStatus = statusFilter === 'Todos'
    ? orders.filter(o => o.workStatus !== 'Finalizado')
    : orders.filter(o => o.workStatus === statusFilter);
  const byStore = storeFilter
    ? byStatus.filter(o => o.storeId === storeFilter)
    : byStatus;
  const filteredOrders = orderNumberSearch.trim()
    ? byStore.filter(o =>
        (o.orderNumber || '').toLowerCase().includes(orderNumberSearch.trim().toLowerCase())
      )
    : byStore;

  // Lojas únicas para o filtro (a partir dos pedidos carregados)
  const storeOptions = React.useMemo(() => {
    const map = new Map<string, string>();
    orders.forEach(o => {
      if (o.storeId && o.storeName) map.set(o.storeId, o.storeName);
      else if (o.storeId) map.set(o.storeId, o.storeId);
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [orders]);

  // Permissões: Designer próprio edita tudo; Suporte (via Atendimento Arte) edita SOMENTE Prioridade; Admin vendo outro = só leitura
  const canEditNonPriority = !viewDesignerId && user?.role === 'DESIGNER';
  const canEditPriority = canEditNonPriority || isSupportOrManagerViewingDesigner;

  // --- Helper functions ---
  const getDesignerStatusColor = (status: DesignerStatus) => {
    switch (status) {
      case 'Suave': return '#10b981';
      case 'Fluxo Bom': return '#3b82f6';
      case 'Correria': return '#f59e0b';
      case 'Sobrecarregado': return '#ef4444';
      case 'Precisando de Apoio': return '#dc2626';
      default: return '#6b7280';
    }
  };

  const getDesignerStatusIcon = (status: DesignerStatus) => {
    switch (status) {
      case 'Suave': return '\u{1F60A}';
      case 'Fluxo Bom': return '\u{1F600}';
      case 'Correria': return '\u{26A1}';
      case 'Sobrecarregado': return '\u{1F630}';
      case 'Precisando de Apoio': return '\u{1F198}';
      default: return '\u{2753}';
    }
  };

  const getWorkStatusColor = (ws: Order['workStatus']) => {
    switch (ws) {
      case 'Aguardando': return '#f59e0b';
      case 'Em Andamento': return '#3b82f6';
      case 'Finalizado': return '#10b981';
      case 'Sem Retorno': return '#ef4444';
      case 'Aguardando Ap': return '#ec4899';
      case 'Aguardando arte': return '#8b5cf6';
      case 'Vendedor': return '#6b7280';
      case 'Suporte': return '#1e3a8a';
      default: return '#6b7280';
    }
  };

  const getWorkStatusIcon = (ws: Order['workStatus']) => {
    switch (ws) {
      case 'Aguardando': return '\u23F3';
      case 'Em Andamento': return '\u{1F3A8}';
      case 'Finalizado': return '\u2705';
      case 'Sem Retorno': return '\u274C';
      case 'Aguardando Ap': return '\u{1FA77}';
      case 'Aguardando arte': return '\u{1F7E3}';
      case 'Vendedor': return '\u{1F9D1}\u200D\u{1F4BC}';
      case 'Suporte': return '\u{1F6E0}\uFE0F';
      default: return '\u{1F4CC}';
    }
  };

  const getWorkStatusDescription = (ws: Order['workStatus']) => {
    switch (ws) {
      case 'Aguardando': return 'Pedido recebido, aguardando início do trabalho';
      case 'Em Andamento': return 'Trabalhando ativamente neste pedido';
      case 'Finalizado': return 'Trabalho concluído e pronto para entrega';
      case 'Sem Retorno': return 'Cliente não respondeu após contato';
      case 'Aguardando Ap': return 'Aguardando aprovação (AP) para seguir com o pedido';
      case 'Aguardando arte': return 'Aguardando envio/ajuste de arte para continuar';
      case 'Vendedor': return 'Pedido aguardando ação do vendedor';
      case 'Suporte': return 'Pedido aguardando ação do suporte';
      default: return '';
    }
  };

  const getPriorityColor = (p: Order['priority']) => {
    switch (p) {
      case 'Baixa': return '#10b981';
      case 'Normal': return '#3b82f6';
      case 'Alta': return '#f59e0b';
      case 'Urgente': return '#ef4444';
      default: return '#6b7280';
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b', mb: 1 }}>
          {(isViewingOtherDesigner || isSupportOrManagerViewingDesigner)
            ? (viewDesignerName ? `Pedidos de ${viewDesignerName}` : 'Pedidos do Designer')
            : 'Atendimento de Designer'}
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          {(isViewingOtherDesigner || isSupportOrManagerViewingDesigner)
            ? (viewDesignerName
                ? `Visualizando pedidos do designer ${viewDesignerName}`
                : 'Visualizando pedidos do designer selecionado')
            : 'Gerencie seus pedidos e informe sua carga de trabalho aos demais usuários'}
        </Typography>
        {isViewingOtherDesigner && !isSupportOrManagerViewingDesigner && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <strong>Modo somente leitura:</strong> Você está visualizando os pedidos deste designer. Apenas a visualização de detalhes está disponível.
          </Alert>
        )}
        {isSupportOrManagerViewingDesigner && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <strong>Modo visualização:</strong> Você pode alterar <strong>somente a coluna Prioridade</strong> dos pedidos. Demais colunas são somente leitura.
          </Alert>
        )}
      </Box>

      {/* Meu Status Atual - só para designer próprio */}
      {canEditNonPriority && (
        <Card sx={{ mb: 4, boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)', border: `2px solid ${getDesignerStatusColor(myCurrentStatus)}` }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ width: 60, height: 60, borderRadius: '12px', backgroundColor: `${getDesignerStatusColor(myCurrentStatus)}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '32px' }}>
                  {getDesignerStatusIcon(myCurrentStatus)}
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', fontSize: '11px', fontWeight: 600 }}>
                    Minha Carga de Trabalho
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: getDesignerStatusColor(myCurrentStatus) }}>
                    {myCurrentStatus}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                    Este status é visível para o comercial distribuir pedidos
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  startIcon={<EditIcon />}
                  onClick={() => setOpenStatusDialog(true)}
                  sx={{ backgroundColor: getDesignerStatusColor(myCurrentStatus), '&:hover': { backgroundColor: getDesignerStatusColor(myCurrentStatus), filter: 'brightness(0.9)' }, textTransform: 'none', fontWeight: 600, px: 3 }}
                >
                  Alterar Meu Status
                </Button>
                <Button
                  variant="contained"
                  startIcon={<FinalizeIcon />}
                  onClick={handleOpenFinalizeDialog}
                  sx={{ backgroundColor: '#10b981', color: '#fff', fontWeight: 600, '&:hover': { backgroundColor: '#059669' } }}
                >
                  Finalizar Dia
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Status Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Paper
            onClick={() => setStatusFilter(statusFilter === 'Aguardando' ? 'Todos' : 'Aguardando')}
            sx={{ p: 2, textAlign: 'center', backgroundColor: statusFilter === 'Aguardando' ? '#fef3c7' : '#fffbeb', border: statusFilter === 'Aguardando' ? '2px solid #f59e0b' : '1px solid #fde68a', cursor: 'pointer', transition: 'all 0.2s', '&:hover': { transform: 'scale(1.02)' } }}
          >
            <Typography variant="h4" sx={{ mb: 0.5, lineHeight: 1 }}>&#9203;</Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#f59e0b' }}>{workStatusCounts.aguardando}</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Aguardando</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper
            onClick={() => setStatusFilter(statusFilter === 'Em Andamento' ? 'Todos' : 'Em Andamento')}
            sx={{ p: 2, textAlign: 'center', backgroundColor: statusFilter === 'Em Andamento' ? '#dbeafe' : '#eff6ff', border: statusFilter === 'Em Andamento' ? '2px solid #3b82f6' : '1px solid #93c5fd', cursor: 'pointer', transition: 'all 0.2s', '&:hover': { transform: 'scale(1.02)' } }}
          >
            <Typography variant="h4" sx={{ mb: 0.5, lineHeight: 1 }}>&#127912;</Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#3b82f6' }}>{workStatusCounts.emAndamento}</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Em Andamento</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper
            onClick={() => setStatusFilter(statusFilter === 'Sem Retorno' ? 'Todos' : 'Sem Retorno')}
            sx={{ p: 2, textAlign: 'center', backgroundColor: statusFilter === 'Sem Retorno' ? '#fee2e2' : '#fef2f2', border: statusFilter === 'Sem Retorno' ? '2px solid #ef4444' : '1px solid #fca5a5', cursor: 'pointer', transition: 'all 0.2s', '&:hover': { transform: 'scale(1.02)' } }}
          >
            <PhoneDisabledIcon sx={{ fontSize: 32, color: '#ef4444', mb: 0.5 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#ef4444' }}>{workStatusCounts.semRetorno}</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Sem Retorno</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper
            onClick={() => setStatusFilter(statusFilter === 'Finalizado' ? 'Todos' : 'Finalizado')}
            sx={{ p: 2, textAlign: 'center', backgroundColor: '#f3e8ff', border: '1px solid #d8b4fe', cursor: 'pointer', transition: 'all 0.2s', '&:hover': { transform: 'scale(1.02)' } }}
          >
            <PaletteIcon sx={{ fontSize: 32, color: '#8b5cf6', mb: 0.5 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#8b5cf6' }}>{totalArts + (parseInt(externalArtsInput) || 0)}</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Total de Artes{(parseInt(externalArtsInput) || 0) > 0 ? ` (${totalArts} + ${parseInt(externalArtsInput) || 0} externas)` : ''}</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Filter Chips */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        {(['Todos', ...WORK_STATUS_OPTIONS] as WorkStatusFilter[]).map((filter) => (
          <Chip
            key={filter}
            label={filter === 'Todos' ? `Todos (${orders.filter(o => o.workStatus !== 'Finalizado').length})` : `${filter} (${orders.filter(o => o.workStatus === filter).length})`}
            onClick={() => setStatusFilter(filter)}
            variant={statusFilter === filter ? 'filled' : 'outlined'}
            sx={{
              fontWeight: statusFilter === filter ? 700 : 400,
              backgroundColor: statusFilter === filter ? (filter === 'Todos' ? '#1e293b' : `${getWorkStatusColor(filter as Order['workStatus'])}20`) : 'transparent',
              color: statusFilter === filter ? (filter === 'Todos' ? '#fff' : getWorkStatusColor(filter as Order['workStatus'])) : '#64748b',
              borderColor: filter === 'Todos' ? '#94a3b8' : getWorkStatusColor(filter as Order['workStatus']),
            }}
          />
        ))}
      </Box>

      {/* Filtro por Loja e busca por número do pedido */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        {storeOptions.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Loja</InputLabel>
            <Select
              value={storeFilter}
              label="Loja"
              onChange={(e) => setStoreFilter(e.target.value)}
              sx={{ backgroundColor: '#fff', borderRadius: 1 }}
            >
              <MenuItem value="">Todas as lojas</MenuItem>
              {storeOptions.map(({ id, name }) => (
                <MenuItem key={id} value={id}>{name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <TextField
          size="small"
          placeholder="Buscar por número do pedido"
          value={orderNumberSearch}
          onChange={(e) => setOrderNumberSearch(e.target.value)}
          sx={{ minWidth: 240, '& .MuiOutlinedInput-root': { backgroundColor: '#fff', borderRadius: 1 } }}
          InputProps={{ sx: { fontSize: '0.9rem' } }}
        />
        <TextField
          size="small"
          type="number"
          label="Artes por fora (Ex: Shopee)"
          value={externalArtsInput}
          onChange={(e) => setExternalArtsInput(e.target.value)}
          onBlur={saveExternalArts}
          onKeyDown={(e) => { if (e.key === 'Enter') { (e.target as HTMLInputElement).blur(); } }}
          disabled={!canEditExternal || externalArtsSaving}
          inputProps={{ min: 0 }}
          title={canEditExternal ? 'Soma no total de artes do mês (referente a hoje)' : 'Somente leitura'}
          sx={{ minWidth: 210, '& .MuiOutlinedInput-root': { backgroundColor: '#fff', borderRadius: 1 } }}
        />
      </Box>

      {/* Tabela de Pedidos */}
      <Card sx={{ boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)' }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
            Meus Pedidos {statusFilter !== 'Todos' && `- ${statusFilter}`}
          </Typography>

          {ordersBlockedMessage && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {ordersBlockedMessage}
            </Alert>
          )}

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Pedido</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Cliente</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Documento</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Contato</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status Trabalho</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Qtd. Artes</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Prioridade</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Envio</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Prazo</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingOrders ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>Carregando pedidos...</Typography>
                    </TableCell>
                  </TableRow>
                ) : filteredOrders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                      {ordersBlockedMessage ? (
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {ordersBlockedMessage}
                        </Typography>
                      ) : (
                        <>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Nenhum pedido encontrado {statusFilter !== 'Todos' ? `com status "${statusFilter}"` : 'para você no momento'}.
                          </Typography>
                          {statusFilter === 'Todos' && (
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
                              Os pedidos do IRROBA com seu status de "Designer responsável" aparecerão aqui.
                            </Typography>
                          )}
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ) : filteredOrders.map((order) => (
                  <TableRow key={order.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>{order.orderNumber}</Typography>
                        {order.source === 'irroba' && (
                          <Chip label={order.storeName || 'IRROBA'} size="small" sx={{ fontSize: '10px', height: '18px' }} color="info" />
                        )}
                      </Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}>
                        R$ {order.orderValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <PersonIcon sx={{ fontSize: 16, color: '#64748b' }} />
                        <Typography variant="body2">{order.clientName}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '13px' }}>{order.clientDocument}</Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <PhoneIcon sx={{ fontSize: 14, color: '#10b981' }} />
                        <Typography variant="body2" sx={{ fontSize: '13px' }}>{order.clientPhone}</Typography>
                        {order.clientPhone && order.clientPhone !== '-' && (
                          <Tooltip title="Copiar telefone">
                            <IconButton
                              size="small"
                              onClick={() => {
                                navigator.clipboard.writeText(order.clientPhone).then(() => {
                                  setSnackbar({ open: true, message: 'Telefone copiado!', severity: 'success' });
                                }).catch(() => {
                                  setSnackbar({ open: true, message: 'Não foi possível copiar.', severity: 'error' });
                                });
                              }}
                              sx={{ p: 0.25, color: '#64748b', '&:hover': { color: '#10b981' } }}
                            >
                              <ContentCopyIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={order.workStatus}
                        size="small"
                        onClick={canEditNonPriority ? () => handleOpenDialog(order, 'workStatus') : undefined}
                        sx={{
                          backgroundColor: `${getWorkStatusColor(order.workStatus)}20`,
                          color: getWorkStatusColor(order.workStatus),
                          fontWeight: 600,
                          cursor: canEditNonPriority ? 'pointer' : 'default',
                          fontSize: '11px',
                          opacity: canEditNonPriority ? 1 : 0.8,
                          '&:hover': canEditNonPriority ? { backgroundColor: `${getWorkStatusColor(order.workStatus)}30` } : {},
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <PaletteIcon sx={{ fontSize: 16, color: '#8b5cf6' }} />
                        <TextField
                          type="number"
                          value={order.artCount}
                          onChange={(e) => {
                            if (!canEditNonPriority) return;
                            const newCount = parseInt(e.target.value) || 0;
                            setOrders(orders.map(o => o.id === order.id ? { ...o, artCount: newCount } : o));
                          }}
                          onBlur={(e) => {
                            if (!canEditNonPriority) return;
                            const newCount = parseInt(e.target.value) || 0;
                            handleArtCountBlur(order.id, newCount);
                          }}
                          size="small"
                          disabled={!canEditNonPriority}
                          inputProps={{ min: 0, style: { textAlign: 'center', width: '50px', padding: '4px 8px', fontSize: '13px', fontWeight: 600 } }}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '6px', backgroundColor: canEditNonPriority ? '#f9fafb' : '#f3f4f6' } }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={order.priority}
                        size="small"
                        icon={<FlagIcon />}
                        onClick={canEditPriority ? () => handleOpenDialog(order, 'priority') : undefined}
                        sx={{
                          backgroundColor: `${getPriorityColor(order.priority)}20`,
                          color: getPriorityColor(order.priority),
                          fontWeight: 600,
                          cursor: canEditPriority ? 'pointer' : 'default',
                          opacity: canEditPriority ? 1 : 0.8,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={order.shippingMethod}
                        size="small"
                        onClick={canEditNonPriority ? () => handleOpenDialog(order, 'shipping') : undefined}
                        sx={{ backgroundColor: '#f3f4f6', color: '#374151', fontWeight: 500, fontSize: '11px', cursor: canEditNonPriority ? 'pointer' : 'default' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={formatDateTimeBR(order.enteredAt)}
                        size="small"
                        sx={{ backgroundColor: '#f0f9ff', color: '#0369a1', fontWeight: 500, fontSize: '11px', cursor: 'default' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Ver Detalhes">
                        <IconButton size="small" onClick={() => handleOpenDialog(order, 'details')} sx={{ color: '#3b82f6' }}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Dialog para edição geral */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        {dialogType === 'details' && selectedOrder && (
          <>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InventoryIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Detalhes do Pedido</Typography>
              </Box>
              <IconButton onClick={handleCloseDialog} size="small"><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 0.5 }}>Número do Pedido</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedOrder.orderNumber}</Typography>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>Informações do Cliente</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pl: 2 }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <PersonIcon sx={{ fontSize: 18, color: '#64748b' }} />
                      <Typography variant="body2">{selectedOrder.clientName}</Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      <strong>CPF/CNPJ:</strong> {selectedOrder.clientDocument}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <PhoneIcon sx={{ fontSize: 16, color: '#10b981' }} />
                      <Typography variant="body2">{selectedOrder.clientPhone}</Typography>
                      {selectedOrder.clientPhone && selectedOrder.clientPhone !== '-' && (
                        <Tooltip title="Copiar telefone">
                          <IconButton size="small" onClick={() => {
                            navigator.clipboard.writeText(selectedOrder.clientPhone).then(() => {
                              setSnackbar({ open: true, message: 'Telefone copiado!', severity: 'success' });
                            });
                          }} sx={{ p: 0.25, color: '#64748b' }}>
                            <ContentCopyIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 0.5 }}>Observação do Cliente</Typography>
                  <Typography variant="body2" sx={{ 
                    whiteSpace: 'pre-wrap',
                    backgroundColor: selectedOrder.comment ? '#fffde7' : 'transparent',
                    p: selectedOrder.comment ? 1.5 : 0,
                    borderRadius: 1,
                    border: selectedOrder.comment ? '1px solid #fff9c4' : 'none',
                    fontStyle: selectedOrder.comment ? 'normal' : 'italic',
                    color: selectedOrder.comment ? 'text.primary' : 'text.secondary',
                  }}>
                    {selectedOrder.comment || 'Nenhuma observação informada pelo cliente'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1, fontWeight: 600 }}>
                    Descrição do Pedido (Itens)
                  </Typography>
                  <OrderDescriptionDisplay
                  description={selectedOrder.description}
                  storeId={selectedOrder.source === 'irroba' ? selectedOrder.storeId : undefined}
                  irrobaOrderId={selectedOrder.irrobaOrderId}
                />
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 0.5 }}>Valor do Pedido</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#10b981' }}>
                    R$ {selectedOrder.orderValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </Typography>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>Quantidade de Artes Produzidas</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <PaletteIcon sx={{ fontSize: 32, color: '#8b5cf6' }} />
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#8b5cf6' }}>{selectedOrder.artCount}</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {selectedOrder.artCount === 1 ? 'arte' : 'artes'}
                    </Typography>
                  </Box>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 0.5 }}>Prazo</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>{formatDateTimeBR(selectedOrder.enteredAt)}</Typography>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}><Button onClick={handleCloseDialog}>Fechar</Button></DialogActions>
          </>
        )}

        {dialogType === 'shipping' && selectedOrder && (
          <>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ShippingIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Alterar Forma de Envio</Typography>
              </Box>
              <IconButton onClick={handleCloseDialog} size="small"><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                Pedido: <strong>{selectedOrder.orderNumber}</strong>
              </Typography>
              <FormControl fullWidth>
                <InputLabel>Forma de Envio</InputLabel>
                <Select value={selectedOrder.shippingMethod} label="Forma de Envio" disabled={!canEditNonPriority}
                  onChange={(e) => handleUpdateShipping(e.target.value as Order['shippingMethod'])}>
                  <MenuItem value="Retirada">Retirada</MenuItem>
                  <MenuItem value="Entrega Local">Entrega Local</MenuItem>
                  <MenuItem value="Transportadora">Transportadora</MenuItem>
                  <MenuItem value="Sedex">Sedex</MenuItem>
                  <MenuItem value="PAC">PAC</MenuItem>
                </Select>
              </FormControl>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={handleCloseDialog}>Cancelar</Button>
              <Button variant="contained" onClick={handleCloseDialog}>Salvar</Button>
            </DialogActions>
          </>
        )}

        {dialogType === 'priority' && selectedOrder && (
          <>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FlagIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Alterar Prioridade</Typography>
              </Box>
              <IconButton onClick={handleCloseDialog} size="small"><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                Pedido: <strong>{selectedOrder.orderNumber}</strong>
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {(['Baixa', 'Normal', 'Alta', 'Urgente'] as Order['priority'][]).map((priority) => (
                  <Paper key={priority} onClick={() => canEditPriority && handleUpdatePriority(priority)}
                    sx={{ p: 2, cursor: 'pointer', border: selectedOrder.priority === priority ? `2px solid ${getPriorityColor(priority)}` : '1px solid #e5e7eb', backgroundColor: selectedOrder.priority === priority ? `${getPriorityColor(priority)}10` : 'white', '&:hover': { backgroundColor: `${getPriorityColor(priority)}20`, transform: 'scale(1.02)' }, transition: 'all 0.2s' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <FlagIcon sx={{ color: getPriorityColor(priority), fontSize: 32 }} />
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: getPriorityColor(priority) }}>{priority}</Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {priority === 'Baixa' && 'Sem urgência, prazo confortável'}
                          {priority === 'Normal' && 'Fluxo padrão de trabalho'}
                          {priority === 'Alta' && 'Requer atenção prioritária'}
                          {priority === 'Urgente' && 'Máxima prioridade, prazo curto'}
                        </Typography>
                      </Box>
                      {selectedOrder.priority === priority && <CheckCircleIcon sx={{ color: getPriorityColor(priority), ml: 'auto' }} />}
                    </Box>
                  </Paper>
                ))}
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}><Button onClick={handleCloseDialog}>Cancelar</Button></DialogActions>
          </>
        )}
      </Dialog>

      {/* Dialog para alterar STATUS DO TRABALHO */}
      {dialogType === 'workStatus' && selectedOrder && (
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Alterar Status do Trabalho</Typography>
            <IconButton onClick={handleCloseDialog} size="small"><CloseIcon /></IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
              Pedido: <strong>{selectedOrder.orderNumber}</strong> - {selectedOrder.clientName}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {WORK_STATUS_OPTIONS.map((ws) => (
                <Paper key={ws}
                  onClick={canEditNonPriority ? () => handleUpdateWorkStatus(ws) : undefined}
                  sx={{
                    p: 2.5, cursor: canEditNonPriority ? 'pointer' : 'default',
                    border: selectedOrder.workStatus === ws ? `3px solid ${getWorkStatusColor(ws)}` : '2px solid #e5e7eb',
                    backgroundColor: selectedOrder.workStatus === ws ? `${getWorkStatusColor(ws)}15` : 'white',
                    opacity: canEditNonPriority ? 1 : 0.6, pointerEvents: canEditNonPriority ? 'auto' : 'none',
                    '&:hover': canEditNonPriority ? { backgroundColor: `${getWorkStatusColor(ws)}20`, transform: 'scale(1.02)', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' } : {},
                    transition: 'all 0.2s',
                  }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ width: 50, height: 50, borderRadius: '10px', backgroundColor: `${getWorkStatusColor(ws)}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '28px' }}>
                      {getWorkStatusIcon(ws)}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 700, color: getWorkStatusColor(ws), mb: 0.5 }}>{ws}</Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '13px' }}>
                        {getWorkStatusDescription(ws)}
                      </Typography>
                    </Box>
                    {selectedOrder.workStatus === ws && <CheckCircleIcon sx={{ color: getWorkStatusColor(ws), fontSize: 32 }} />}
                  </Box>
                </Paper>
              ))}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}><Button onClick={handleCloseDialog}>Cancelar</Button></DialogActions>
        </Dialog>
      )}

      {/* Dialog para alterar DATA DE FINALIZAÇÃO */}
      {dialogType === 'deadline' && selectedOrder && (
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Alterar Data de Finalização</Typography>
            <IconButton onClick={handleCloseDialog} size="small"><CloseIcon /></IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
              Pedido: <strong>{selectedOrder.orderNumber}</strong> - {selectedOrder.clientName}
            </Typography>
            <TextField fullWidth type="date" label="Data de Finalização" defaultValue={selectedOrder.deadline}
              onChange={(e) => { if (selectedOrder) setSelectedOrder({ ...selectedOrder, deadline: e.target.value }); }}
              InputLabelProps={{ shrink: true }} helperText="Defina quando este trabalho deve ser finalizado" />
            <Paper sx={{ mt: 3, p: 2, backgroundColor: '#eff6ff', border: '1px solid #bfdbfe' }}>
              <Typography variant="caption" sx={{ color: '#1e40af', display: 'block', fontWeight: 600, mb: 0.5 }}>Data Atual:</Typography>
              <Typography variant="body2" sx={{ color: '#1e40af', fontWeight: 600 }}>{formatDateLongBR(selectedOrder.deadline)}</Typography>
            </Paper>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseDialog}>Cancelar</Button>
            <Button variant="contained" onClick={() => { if (selectedOrder) handleUpdateDeadline(selectedOrder.deadline); }}>Salvar Data</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Dialog para alterar MEU status pessoal */}
      <Dialog open={openStatusDialog} onClose={() => setOpenStatusDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Como está sua carga de trabalho?</Typography>
          <IconButton onClick={() => setOpenStatusDialog(false)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
            Informe à equipe como está sua disponibilidade para receber novos pedidos.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {(['Suave', 'Fluxo Bom', 'Correria', 'Sobrecarregado', 'Precisando de Apoio'] as DesignerStatus[]).map((status) => (
              <Paper key={status} onClick={loadingStatus ? undefined : () => handleStatusChange(status)}
                sx={{ p: 2.5, cursor: loadingStatus ? 'not-allowed' : 'pointer', opacity: loadingStatus ? 0.7 : 1, border: myCurrentStatus === status ? `3px solid ${getDesignerStatusColor(status)}` : '2px solid #e5e7eb', backgroundColor: myCurrentStatus === status ? `${getDesignerStatusColor(status)}15` : 'white', '&:hover': loadingStatus ? undefined : { backgroundColor: `${getDesignerStatusColor(status)}20`, transform: 'scale(1.02)', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }, transition: 'all 0.2s' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ width: 50, height: 50, borderRadius: '10px', backgroundColor: `${getDesignerStatusColor(status)}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '28px' }}>
                    {getDesignerStatusIcon(status)}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: getDesignerStatusColor(status), mb: 0.5 }}>{status}</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '13px' }}>
                      {status === 'Suave' && 'Poucos pedidos, posso receber mais trabalho tranquilamente'}
                      {status === 'Fluxo Bom' && 'Ritmo normal, ainda posso receber novos pedidos'}
                      {status === 'Correria' && 'Muitos pedidos, cuidado ao encaminhar novos trabalhos'}
                      {status === 'Sobrecarregado' && 'Volume muito alto, evitar novos pedidos'}
                      {status === 'Precisando de Apoio' && 'Necessário urgente redistribuir meus pedidos'}
                    </Typography>
                  </Box>
                  {myCurrentStatus === status && <CheckCircleIcon sx={{ color: getDesignerStatusColor(status), fontSize: 32 }} />}
                </Box>
              </Paper>
            ))}
          </Box>
          <Paper sx={{ mt: 3, p: 2, backgroundColor: '#fef3c7', border: '1px solid #fde68a' }}>
            <Typography variant="caption" sx={{ color: '#92400e', display: 'block', fontWeight: 600, mb: 0.5 }}>Importante:</Typography>
            <Typography variant="caption" sx={{ color: '#92400e', display: 'block' }}>Este status ajuda o comercial a distribuir pedidos de forma equilibrada</Typography>
            <Typography variant="caption" sx={{ color: '#92400e', display: 'block' }}>Mantenha sempre atualizado para evitar sobrecarga</Typography>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}><Button onClick={() => setOpenStatusDialog(false)}>Cancelar</Button></DialogActions>
      </Dialog>

      {/* Dialog: Finalizar Dia */}
      <Dialog open={openFinalizeDialog} onClose={() => setOpenFinalizeDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#10b981', color: '#fff', fontWeight: 600 }}>
          Finalizar Dia
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <FinalizeIcon sx={{ fontSize: 64, color: '#10b981', mb: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              Deseja finalizar o dia de hoje?
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b7280', mb: 3 }}>
              Ao finalizar, o total de artes será salvo no seu histórico de produtividade.
            </Typography>

            <Box sx={{ backgroundColor: 'rgba(139, 92, 246, 0.05)', borderRadius: 2, p: 2, border: '1px solid rgba(139, 92, 246, 0.2)', mb: 2 }}>
              <Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>Resumo do Dia:</Typography>
              <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 1 }}>
                Soma automática dos pedidos: {orders.reduce((sum, o) => sum + o.artCount, 0)} artes
              </Typography>
              <TextField
                type="number"
                label="Total de Artes (ajustável)"
                value={finalizeArtsCount}
                onChange={(e) => setFinalizeArtsCount(parseInt(e.target.value) || 0)}
                fullWidth
                size="small"
                inputProps={{ min: 0 }}
                sx={{ mt: 1 }}
                helperText="Você pode ajustar o total se necessário"
              />
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3 }}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#8b5cf6' }}>{finalizeArtsCount}</Typography>
                <Typography variant="caption" sx={{ color: '#6b7280' }}>artes</Typography>
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#3b82f6' }}>{orders.length}</Typography>
                <Typography variant="caption" sx={{ color: '#6b7280' }}>pedidos</Typography>
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#10b981' }}>{workStatusCounts.finalizado}</Typography>
                <Typography variant="caption" sx={{ color: '#6b7280' }}>finalizados</Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenFinalizeDialog(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleFinalizeDay}
            sx={{ backgroundColor: '#10b981', color: '#fff', '&:hover': { backgroundColor: '#059669' } }}
          >
            Confirmar Finalização
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DesignerInterface;
